// Home's awards widget (AWARDS.md "on Home"): the champion banner once the
// season is decided, then this period's awards: mine as cards, the rest as
// a compact list, "as it stands" while the games are on.

import { Fragment, useState, type ReactNode } from 'react';
import type { AwardCardView, AwardsView, TeamRef } from '@fsp/domain';
import { useNavigate } from 'react-router-dom';
import { useLeagueBase } from '../../data/hooks';
import { LiveDot, TeamMark } from '../../kit';
import { Panel } from '../../kit/Panel';
import { Button, Cover, ListRow, RowName, RowSub, Text } from '../../kit/ui';
import { AwardCard, AwardDetail, AwardIcon, Cup } from './AwardKit';
import { TeamName } from './Names';

export function AwardsWidget({
  awards,
  meta,
}: {
  awards: AwardsView;
  /** On the awards' panel's head: the front page's Expand (B-403). */
  meta?: ReactNode;
}) {
  const base = useLeagueBase();
  const navigate = useNavigate();
  const [open, setOpen] = useState<AwardCardView | null>(null);
  const toRoom = (team: TeamRef) => navigate(`${base}/awards?tab=room&team=${team.id}`);
  const champion = awards.season.champion;
  const live = awards.status === 'live';
  const mine = awards.mine;
  const others = awards.perPeriod.filter((c) => !c.winners.some((w) => w.team.isMine));
  const nothing = awards.status === 'pre' || awards.perPeriod.length === 0;

  return (
    <>
      {champion ? (
        <Panel title={`${champion.season} champion`}>
          <div className="fsp-surface fsp-champion-banner fsp-row-clickable fsp-cover-host">
            {/* The banner opens the trophy room; the name opens the team (B-220). */}
            <Cover
              label={`${champion.team.name}'s trophy room`}
              onClick={() => toRoom(champion.team)}
            />
            <Cup
              primary={champion.team.colors.primary}
              secondary={champion.team.colors.secondary}
              size={48}
            />
            <div className="fsp-champion-banner-body">
              <Text weight="bold" block>
                <TeamName team={champion.team} look="inline" /> are your {champion.season}{' '}
                {champion.trophyName}.
              </Text>
              <div className="fsp-sub fsp-ui-wrap-text">{champion.line}</div>
            </div>
            <TeamMark team={champion.team} size={28} />
          </div>
        </Panel>
      ) : null}
      <Panel
        title={awards.status === 'final' ? `${awards.period.label} awards` : 'Awards'}
        meta={meta}
      >
        {nothing ? (
          <div className="fsp-widget-note">
            <Text tone="secondary">
              {awards.status === 'final'
                ? 'Too few games this week for weekly awards. The season awards are the story.'
                : 'Awards are handed out once the week’s scores are final.'}
            </Text>
          </div>
        ) : (
          <>
            {live ? (
              <div className="fsp-sub fsp-widget-live">
                <LiveDot />
                As it stands. Handed out when the last game ends.
              </div>
            ) : null}
            {mine.map((c) => (
              <div key={c.id} className="fsp-widget-card">
                <AwardCard card={c} compact onOpen={setOpen} />
              </div>
            ))}
            {others.slice(0, mine.length ? 3 : 4).map((c) => (
              <ListRow
                key={c.id}
                leadSize="md"
                lead={
                  <span className="fsp-award-icon fsp-widget-glyph" data-tone={c.tone}>
                    <AwardIcon icon={c.icon} size={14} />
                  </span>
                }
                trail={
                  <Text weight="bold" numeric>
                    {c.winners[0]?.valueLabel}
                  </Text>
                }
                onClick={() => setOpen(c)}
                cover={`${c.name}: the award`}
              >
                <RowName>{c.name}</RowName>
                <RowSub>
                  {c.winners.map((w, i) => (
                    <Fragment key={w.team.id}>
                      {i > 0 ? ' and ' : ''}
                      <TeamName team={w.team} />
                    </Fragment>
                  ))}
                </RowSub>
              </ListRow>
            ))}
            <div className="fsp-widget-links">
              <Button to={`${base}/awards`} variant="quiet" size="xs">
                All awards
              </Button>
              <Button to={`${base}/awards?tab=room`} variant="quiet" size="xs">
                My trophy room
              </Button>
            </div>
          </>
        )}
      </Panel>
      <AwardDetail
        card={open}
        league={{ name: awards.league.name, season: awards.league.season }}
        onClose={() => setOpen(null)}
        onTeam={(t) => {
          setOpen(null);
          toRoom(t);
        }}
      />
    </>
  );
}
